import { useEffect, useState } from 'react'
import { useQuery } from 'react-query'
import { fetchSpotPrice, SpotPrice } from 'api/inventory'
import { useAuth } from 'components/AuthProvider'

export interface UseSpotPriceProps {
  callInterval?: number // milliseconds
  onCompleted?: (data: SpotPrice) => void
  productId: number
}

type UseSpotPriceReturnType = [
  () => void,
  {
    data: SpotPrice | undefined
    loading: boolean
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    error: any
    getProgress: () => number
  }
]

export default function useSpotPrice(
  props: UseSpotPriceProps
): UseSpotPriceReturnType {
  const { callInterval, onCompleted, productId } = props || {}

  const { setToast } = useAuth()

  const [lastUpdated, setLastUpdated] = useState<number>(Date.now())

  const {
    data,
    isLoading: loading,
    error,
    refetch
  } = useQuery(['spot-price', productId], () => fetchSpotPrice(productId), {
    staleTime: 30_000,
    onSuccess: data => {
      setLastUpdated(Date.now())
      if (onCompleted) onCompleted(data)
    },
    onError: (err: Error) =>
      setToast({
        open: true,
        message: err.message,
        type: 'error',
        duration: 3000
      })
  })

  function getProgress(): number {
    if (callInterval === undefined) return 100

    const millisecondsEllapsed = Date.now() - lastUpdated
    return ((callInterval - millisecondsEllapsed) / callInterval) * 100
  }

  useEffect(() => {
    if (callInterval) {
      const timer = setInterval(() => {
        setLastUpdated(Date.now())
        refetch()
      }, callInterval)

      return () => clearInterval(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callInterval])

  return [
    () => {
      refetch()
    },
    {
      data,
      loading,
      error,
      getProgress
    }
  ]
}
